import React from 'react';

/**
 * The single in-app definition of what a Prove7 error looks like.
 *
 * Every error surface inside the SPA renders through this: AppErrorBoundary, the per-page
 * boundaries, and the catch-all route. Before it there were three unrelated treatments, and
 * two of them put internals on screen — public/index.html's global overlay rendered the
 * message, source file, line:col and full stack, and AppErrorBoundary rendered error.stack
 * plus the React component stack. Neither was environment-gated, so both shipped to users in
 * production builds.
 *
 * The rule this component enforces: the user reads a branded explanation; the technical
 * detail goes to the console (and telemetry) and reaches the screen only in development.
 * Callers pass `detail` unconditionally and do not gate it themselves — if the decision lived
 * at each call site it would drift at the first new boundary, which is how this got here.
 *
 * Styling is Tailwind over the v2 semantic tokens, like the rest of components/v2, so a theme
 * change moves this surface with everything else instead of stranding it on literal colours.
 */
function ErrorState({ code, title, message, detail = null, actions = null }) {
  const showDetail = detail && process.env.NODE_ENV !== 'production';

  return (
    <div
      data-testid="error-state"
      role="alert"
      className="flex items-center justify-center w-full px-6 py-16"
    >
      <div className="w-full max-w-[560px]">
        {code != null && (
          <div className="text-[13px] font-semibold uppercase tracking-[0.16em] text-v2-brand mb-3">
            {code}
          </div>
        )}

        <h1 className="text-[28px] leading-tight font-bold tracking-tight text-v2-ink m-0 mb-3">
          {title}
        </h1>

        <p className="text-[15px] leading-relaxed text-v2-ink-secondary m-0 mb-7">
          {message}
        </p>

        {actions && <div className="flex gap-2.5 flex-wrap">{actions}</div>}

        {showDetail && (
          <details className="mt-8" data-testid="error-state-detail">
            <summary className="cursor-pointer text-[12px] font-semibold text-v2-ink-muted">
              Technical detail (development only)
            </summary>
            <pre className="mt-2 p-4 rounded-lg bg-v2-muted border border-v2-border-subtle text-[12px] text-v2-ink-secondary overflow-auto max-h-[300px] whitespace-pre-wrap break-words">
              {detail}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default ErrorState;
